/* Auto-built from oxyBurnReward.json — every function of the contract is covered. */
import { fmt } from "../shared/format";

export const readFunctions = [
  {
    name: "burnTradeSigner",
    title: "Burn Trade Signer",
    description: "Read `burnTradeSigner` from the contract",
    icon: "🔥",
    category: "Trading",
    inputs: [],
  },
  {
    name: "eip712Domain",
    title: "EIP712 Domain",
    description: "Read `eip712Domain` from the contract",
    icon: "🔹",
    category: "Basic Info",
    inputs: [],
    formatOutput: (v) => [
      `Fields: ${v.fields ?? v[0]}`,
      `Name: ${v.name ?? v[1]}`,
      `Version: ${v.version ?? v[2]}`,
      `Chain ID: ${v.chainId ?? v[3]}`,
      `Verifying Contract: ${v.verifyingContract ?? v[4]}`,
      `Salt: ${v.salt ?? v[5]}`,
      `Extensions: ${v.extensions ?? v[6]}`,
    ].join("\n"),
  },
  {
    name: "getTradeSingerSign",
    title: "Verify Signer Signature",
    description: "Recover the signer of a burn-trade signature and check that it is valid.",
    icon: "🛒",
    category: "Trading",
    inputs: [
      { name: "nftId", type: "uint256", label: "NFT ID", placeholder: "Enter nft id", required: true },
      { name: "deadline", type: "uint256", label: "Deadline", placeholder: "Enter deadline", required: true },
      { name: "v", type: "uint8", label: "V", placeholder: "Enter v", required: true },
      { name: "r", type: "bytes32", label: "R", placeholder: "0x…", required: false },
      { name: "s", type: "bytes32", label: "S", placeholder: "0x…", required: false },
    ],
    formatOutput: (v) => [`Signer: ${v[0]}`, `Valid: ${fmt.bool(v[1])}`].join("\n"),
  },
  {
    name: "globalMarketPlaceReward",
    title: "Global Market Place Reward",
    description: "Read `globalMarketPlaceReward` from the contract",
    icon: "💰",
    category: "Rewards",
    inputs: [],
    formatOutput: (v) => fmt.amount(v),
  },
  {
    name: "globalMatrixReward",
    title: "Global Matrix Reward",
    description: "Read `globalMatrixReward` from the contract",
    icon: "💰",
    category: "Rewards",
    inputs: [],
    formatOutput: (v) => fmt.amount(v),
  },
  {
    name: "hashBurnTrade",
    title: "Hash Burn Trade",
    description: "EIP-712 digest that the burn-trade signer must sign.",
    icon: "🔥",
    category: "User Info",
    inputs: [
      { name: "nftId", type: "uint256", label: "NFT ID", placeholder: "Enter nft id", required: true },
      { name: "deadline", type: "uint256", label: "Deadline", placeholder: "Enter deadline", required: true },
    ],
  },
  {
    name: "nftMarketPlace",
    title: "NFT Market Place",
    description: "Read `nftMarketPlace` from the contract",
    icon: "🛒",
    category: "Linked Contracts",
    inputs: [],
  },
  {
    name: "nftMatrix",
    title: "NFT Matrix",
    description: "Read `nftMatrix` from the contract",
    icon: "🌳",
    category: "Linked Contracts",
    inputs: [],
  },
  {
    name: "owner",
    title: "Owner",
    description: "Read `owner` from the contract",
    icon: "👑",
    category: "Basic Info",
    inputs: [],
  },
  {
    name: "paused",
    title: "Paused",
    description: "Read `paused` from the contract",
    icon: "⏸️",
    category: "Basic Info",
    inputs: [],
    formatOutput: (v) => fmt.bool(v),
  },
  {
    name: "usdtToken",
    title: "USDT Token",
    description: "Read `usdtToken` from the contract",
    icon: "🪙",
    category: "Basic Info",
    inputs: [],
  },
];

export const writeFunctions = [
  {
    name: "burnTrade",
    title: "Burn Trade (Signed)",
    description: "Burn a trade using an EIP-712 signature issued by the burn-trade signer.",
    icon: "🔥",
    category: "User Actions",
    inputs: [
      { name: "nftId", type: "uint256", label: "NFT ID", placeholder: "Enter nft id", required: true },
      { name: "deadline", type: "uint256", label: "Deadline", placeholder: "Enter deadline", required: true, hint: "Unix timestamp after which the signature is no longer valid." },
      { name: "v", type: "uint8", label: "V", placeholder: "27 or 28", required: true },
      { name: "r", type: "bytes32", label: "R", placeholder: "0x… (32 bytes)", required: true },
      { name: "s", type: "bytes32", label: "S", placeholder: "0x… (32 bytes)", required: true },
    ],
  },
];

export const adminFunctions = [
  {
    name: "addMarketPlaceReward",
    title: "Add Market Place Reward",
    description: "Call `addMarketPlaceReward` on the contract",
    icon: "💰",
    category: "Admin Operations",
    inputs: [
      { name: "amount", type: "uint256", label: "Amount", placeholder: "e.g. 10.5", required: true, unit: "ether" },
    ],
  },
  {
    name: "addMatrixReward",
    title: "Add Matrix Reward",
    description: "Call `addMatrixReward` on the contract",
    icon: "💰",
    category: "Admin Operations",
    inputs: [
      { name: "amount", type: "uint256", label: "Amount", placeholder: "e.g. 10.5", required: true, unit: "ether" },
    ],
  },
  {
    name: "initialize",
    title: "Initialize",
    description: "Call `initialize` on the contract",
    icon: "⚙️",
    category: "Admin Control",
    ownerOnly: true,
    inputs: [
      { name: "initialOwner", type: "address", label: "Initial Owner", placeholder: "0x…", required: true },
      { name: "nftMarketPlaceContract", type: "address", label: "NFT Market Place Contract", placeholder: "0x…", required: true },
      { name: "newNftMatrixContract", type: "address", label: "New NFT Matrix Contract", placeholder: "0x…", required: true },
    ],
  },
  {
    name: "newSetup",
    title: "New Setup",
    description: "Call `newSetup` on the contract",
    icon: "⚙️",
    category: "Admin Control",
    ownerOnly: true,
    inputs: [],
  },
  {
    name: "pause",
    title: "Pause",
    description: "Call `pause` on the contract",
    icon: "⏸️",
    category: "Admin Control",
    ownerOnly: true,
    inputs: [],
  },
  {
    name: "recoverErc20",
    title: "Recover ERC20",
    description: "Call `recoverErc20` on the contract",
    icon: "♻️",
    category: "Admin Recovery",
    ownerOnly: true,
    inputs: [
      { name: "erc20Token", type: "address", label: "ERC20 Token", placeholder: "0x…", required: true },
      { name: "amount", type: "uint256", label: "Amount", placeholder: "e.g. 10.5", required: true, unit: "ether" },
    ],
  },
  {
    name: "recoverErc20All",
    title: "Recover ERC20 All",
    description: "Call `recoverErc20All` on the contract",
    icon: "♻️",
    category: "Admin Recovery",
    ownerOnly: true,
    inputs: [
      { name: "erc20Token", type: "address", label: "ERC20 Token", placeholder: "0x…", required: true },
    ],
  },
  {
    name: "recoverEth",
    title: "Recover ETH",
    description: "Call `recoverEth` on the contract",
    icon: "♻️",
    category: "Admin Recovery",
    ownerOnly: true,
    inputs: [
      { name: "amount", type: "uint256", label: "Amount", placeholder: "e.g. 10.5", required: true, unit: "ether" },
    ],
  },
  {
    name: "recoverUsdt",
    title: "Recover USDT",
    description: "Call `recoverUsdt` on the contract",
    icon: "🪙",
    category: "Admin Recovery",
    ownerOnly: true,
    inputs: [
      { name: "amount", type: "uint256", label: "Amount", placeholder: "e.g. 10.5", required: true, unit: "ether" },
    ],
  },
  {
    name: "recoverUsdtAll",
    title: "Recover USDT All",
    description: "Call `recoverUsdtAll` on the contract",
    icon: "🪙",
    category: "Admin Recovery",
    ownerOnly: true,
    inputs: [],
  },
  {
    name: "renounceOwnership",
    title: "Renounce Ownership",
    description: "Call `renounceOwnership` on the contract",
    icon: "👑",
    category: "Admin Control",
    ownerOnly: true,
    inputs: [],
  },
  {
    name: "setBurnTradeSigner",
    title: "Set Burn Trade Signer",
    description: "Call `setBurnTradeSigner` on the contract",
    icon: "🔥",
    category: "Admin Settings",
    ownerOnly: true,
    inputs: [
      { name: "newSigner", type: "address", label: "New Signer", placeholder: "0x…", required: true },
    ],
  },
  {
    name: "setNewNftMarketPlace",
    title: "Set New NFT Market Place",
    description: "Call `setNewNftMarketPlace` on the contract",
    icon: "🛒",
    category: "Admin Settings",
    ownerOnly: true,
    inputs: [
      { name: "newNftMarketPlace", type: "address", label: "New NFT Market Place", placeholder: "0x…", required: true },
    ],
  },
  {
    name: "setNftMatrixContract",
    title: "Set NFT Matrix Contract",
    description: "Call `setNftMatrixContract` on the contract",
    icon: "🌳",
    category: "Admin Settings",
    ownerOnly: true,
    inputs: [
      { name: "newNftMatrix", type: "address", label: "New NFT Matrix", placeholder: "0x…", required: true },
    ],
  },
  {
    name: "setTokenApproveMax",
    title: "Set Token Approve Max",
    description: "Call `setTokenApproveMax` on the contract",
    icon: "🪙",
    category: "Admin Settings",
    ownerOnly: true,
    inputs: [],
  },
  {
    name: "setUsdtToken",
    title: "Set USDT Token",
    description: "Call `setUsdtToken` on the contract",
    icon: "🪙",
    category: "Admin Settings",
    ownerOnly: true,
    inputs: [
      { name: "newToken", type: "address", label: "New Token", placeholder: "0x…", required: true },
    ],
  },
  {
    name: "transferOwnership",
    title: "Transfer Ownership",
    description: "Call `transferOwnership` on the contract",
    icon: "👑",
    category: "Admin Control",
    ownerOnly: true,
    inputs: [
      { name: "newOwner", type: "address", label: "New Owner", placeholder: "0x…", required: true },
    ],
  },
  {
    name: "unpause",
    title: "Unpause",
    description: "Call `unpause` on the contract",
    icon: "▶️",
    category: "Admin Control",
    ownerOnly: true,
    allowWhilePaused: true,
    inputs: [],
  },
];

const burnRewardFunctions = { readFunctions, writeFunctions, adminFunctions };
export default burnRewardFunctions;
